// admin/src/pages/Dashboard/dashboardConstants.js
// Design tokens, color schemes, and formatting helpers for Identity Overview

export const LIGHT_TOKENS = {
  bg: "#F7F9FC",
  bgCard: "#FFFFFF",
  bgCardHover: "#F7F9FC",
  bgMuted: "#F7F9FC",

  border: "#E4E9F0",
  borderHover: "#1F6FEB",

  accent1: "#1F6FEB", // --edge-blue
  accent2: "#00A88F", // --secure
  accent3: "#D6323A", // --critical
  accent4: "#7C5CDB", // purple
  accent5: "#F0A202", // --elevated
  accent6: "#E2622B", // --high

  chartPalette: ["#1F6FEB", "#00A88F", "#7C5CDB", "#F0A202", "#D6323A", "#E2622B", "#EC4899", "#0EA5E9"],

  fontHeading: "Arial, sans-serif",
  fontBody: "Calibri, Arial, sans-serif",
  textPrimary: "#0B2545", // --vault-navy
  textSecondary: "#6B7A90", // --slate
  textMuted: "#8C99AD",

  gridLine: "rgba(11,37,69,0.07)",
  menuBg: "#FFFFFF",
  shadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
};

export const DARK_TOKENS = {
  bg: "#0F1729",
  bgCard: "#252b3b",
  bgCardHover: "#252b3b",
  bgMuted: "#252b3b",

  border: "rgba(255,255,255,0.14)",
  borderHover: "#4C9AFF",

  accent1: "#4C9AFF",
  accent2: "#2FD3B8",
  accent3: "#FF6B72",
  accent4: "#9B8CFF",
  accent5: "#FFC24B",
  accent6: "#F07C46",

  chartPalette: ["#4C9AFF", "#2FD3B8", "#9B8CFF", "#FFC24B", "#FF6B72", "#F07C46", "#FF7AB8", "#3FD0FF"],

  fontHeading: "Arial, sans-serif",
  fontBody: "Calibri, Arial, sans-serif",
  textPrimary: "#F4F7FB",
  textSecondary: "#93A4BD",
  textMuted: "#7C8CA6",

  gridLine: "rgba(255,255,255,0.08)",
  menuBg: "#252b3b",
  shadow: "none",
};

// Fixed color per MFA method across all charts
export const MFA_METHOD_COLORS = {
  push: "#1F6FEB",
  trustedPush: "#1F6FEB",
  biometric: "#00A88F",
  passkey: "#7C5CDB",
  totp: "#F0A202",
  email_otp: "#0EA5E9",
  emailOtp: "#0EA5E9",
  none: "#9CA3AF",
};

// Human-friendly labels for MFA methods
export const MFA_METHOD_LABELS = {
  push: "Push",
  trustedPush: "Push (Device)",
  biometric: "Biometric",
  passkey: "Passkey",
  totp: "TOTP",
  email_otp: "Email OTP",
  emailOtp: "Email OTP",
  none: "None",
};

// Protocol colors
export const PROTOCOL_COLORS = {
  direct: "#1F6FEB",
  oidc: "#7C5CDB",
  saml: "#00A88F",
  custom: "#F0A202",
};

// Severity colors & badge backgrounds
export const SEVERITY_CONFIG = {
  critical: { color: "#D6323A", bgLight: "rgba(214, 50, 58, 0.12)", label: "Critical" },
  high: { color: "#E2622B", bgLight: "rgba(226, 98, 43, 0.12)", label: "High" },
  warning: { color: "#F0A202", bgLight: "rgba(240, 162, 2, 0.12)", label: "Warning" },
  info: { color: "#1F6FEB", bgLight: "rgba(31, 111, 235, 0.12)", label: "Info" },
};

// Format integer with comma or k suffix if large
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 10000) return `${(num / 1000).toFixed(1)}k`;
  return Number(num).toLocaleString();
};

// Format percentage with 1 decimal place; returns "—" if value is null or undefined
export const formatPercent = (val) => {
  if (val === null || val === undefined) return "—";
  if (isNaN(val)) return "0%";
  return `${Number(val).toFixed(1)}%`;
};

// Format milliseconds into seconds or ms
export const formatLatency = (ms) => {
  if (ms === null || ms === undefined || isNaN(ms)) return "—";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.round(ms)}ms`;
};

// Format date into human-friendly string (e.g., Oct 9, 2026)
export const formatDateOnly = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

// Format ISO date / timestamp into hh:mm or MM/DD hh:mm
export const formatTime = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

export const formatDateTime = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return "—";
  }
};

// Permission string
export const DASHBOARD_READ_PERMISSION = "dashboard-Dashboard-DashboardRead";
